/**
 * Зведений реєстр договорів (Довідники) — ag-Grid Infinite + пошук.
 * Колонки: Контрагент / Організація / Назва / Номер / Дата / Строк дії / Валюта.
 *
 * 2026-07-23 (рішення власника): договір відкривається як ОКРЕМА СТОРIНКА
 * (/refs/contracts/:id), не Drawer. Створення — /refs/contracts/new.
 */
import { useMemo, useState } from 'react';
import { useOrgFilter } from '@/store/defaults';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  contractorNested, orgs, type ContractRegistryItem,
} from '@/api/catalogs';
import { refs } from '@/api/refs';
import ImportedBadge from '@/shared/components/ImportedBadge';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';

export default function ContractsRegistryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgFilter, setOrgFilter] = useOrgFilter();

  const { datasource, total } = useInfiniteGridDatasource<ContractRegistryItem>(
    (limit, offset, sort) => contractorNested.listRegistry({
      q: search || undefined, organization_id: orgFilter || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [search, orgFilter],
    { attachmentsType: 'ref.contracts' },
  );

  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies });
  const currCode = (id: string) => (currenciesQ.data ?? []).find((c) => c.id === id)?.code ?? '';

  const cols = useMemo<ColDef<ContractRegistryItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: ContractRegistryItem }) => <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'contractor_name', headerName: 'Контрагент', flex: 1, minWidth: 200, sortable: true },
    { field: 'organization_name', headerName: 'Організація', flex: 1, minWidth: 160, sortable: true,
      valueFormatter: (p) => p.value ?? '—' },
    // Назва (без посилання — відкриття по double-click рядка).
    { field: 'name', headerName: 'Назва договору', flex: 1, minWidth: 180, sortable: true },
    { field: 'number', headerName: 'Номер', width: 120, sortable: true },
    { field: 'contract_date', headerName: 'Дата', width: 120, sortable: true,
      valueFormatter: (p) => p.value ?? '—' },
    { field: 'valid_until', headerName: 'Строк дії', width: 120, sortable: true,
      valueFormatter: (p) => p.value ?? 'безстроковий' },
    { headerName: 'Валюта', width: 90, sortable: false,
      valueGetter: (p) => (p.data ? currCode(p.data.currency_id) : '') },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
  ], [currenciesQ.data, navigate]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Реєстр договорів
          {total !== null && (
            <span style={{ fontSize: 13, color: 'var(--muted)', marginLeft: 8, fontWeight: 400 }}>({total})</span>
          )}
        </h2>
        <div style={{ marginLeft: 'auto' }}>
          <button className="primary" onClick={() => navigate('/refs/contracts/new')}>
            + Новий договір
          </button>
        </div>
      </div>
      {/* ч.691: фільтри — окремим рядком під назвою сторінки (канон). */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0 0.75rem' }}>
        <input type="search" placeholder="Пошук № / назва / контрагент"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
          <option value="">Усі юрособи</option>
          {(orgsQ.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      <InfiniteDocGrid<ContractRegistryItem>
        datasource={datasource}
        columnDefs={cols}
        storageKey="contracts-registry"
        onRowDoubleClicked={(r) => navigate(`/refs/contracts/${r.id}`)}
      />
    </div>
  );
}
